import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useSiteLayout } from '../context/SiteLayoutContext';
import { api } from '../services/api';
import { translations, companyInfo } from '../data/mockData';
import Hero from '../components/Hero';
import TourCard from '../components/TourCard';
import TourListRow, { normalizeForSearch as norm, buildTourHaystack } from '../components/TourListRow';
import HomeGallery from '../components/HomeGallery';
import HomeSpotlight from '../components/HomeSpotlight';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { MapPin, Users, Calendar, Award, DollarSign, HeartHandshake, Cross, Play, X, Quote, ArrowLeftRight, ArrowRight, ChevronRight, MessageCircle, Mail, LayoutGrid, List, Search } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { GOLD, NAVY, CREAM, ALT_BG, INK, alpha } from '../theme/palette';

// Belmond Cathedral theme tokens
const SERIF = { fontFamily: '"Fraunces", serif' };

const Home = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const { layout } = useSiteLayout();
  const homeSections = layout.sections;
  const location = useLocation();
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', message: '' });
  const [tours, setTours] = useState([]);
  const [faqData, setFaqData] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [videoPopup, setVideoPopup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tourTab, setTourTab] = useState('all');
  const [tourQuery, setTourQuery] = useState('');
  const [tourViewMode, setTourViewMode] = useState(() => {
    try {
      // Iter 356b — v2 key so old users get the new mobile-first default.
      const saved = localStorage.getItem('pilgrimages-view-mode-v2');
      if (saved) return saved;
      // Default: list on mobile (<768px), grid on desktop.
      return (typeof window !== 'undefined' && window.innerWidth < 768) ? 'list' : 'grid';
    } catch { return 'grid'; }
  });
  const chooseTourView = (v) => { setTourViewMode(v); try { localStorage.setItem('pilgrimages-view-mode-v2', v); } catch {} };
  const [hpSettings, setHpSettings] = useState(null);

  const whatsappNum = language === 'pt' ? companyInfo.whatsapp : companyInfo.whatsappUS;
  const brOffice = companyInfo.offices.brazil;
  const usOffice = companyInfo.offices.usa;

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (location.state?.scrollTo) {
      const timer = setTimeout(() => {
        const el = document.getElementById(location.state.scrollTo);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  useEffect(() => {
    const path = location.pathname;
    const sectionMap = { '/about': 'about', '/faq': 'faq', '/contact': 'contact' };
    const sectionId = sectionMap[path];
    if (sectionId) {
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [location.pathname]);

  const loadData = async () => {
    try {
      const [toursRes, faqsRes, testimonialsRes, hpRes] = await Promise.all([
        api.getTours(), api.getFAQs(),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/testimonials`).then(r => r.json()).catch(() => []),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/homepage-settings`).then(r => r.json()).catch(() => null),
      ]);
      setTours(toursRes.data.map(tour => ({
        id: tour._id,
        name: { pt: tour.name_pt, en: tour.name_en, es: tour.name_es },
        destination: { pt: tour.destination_pt, en: tour.destination_en, es: tour.destination_es },
        description: { pt: tour.description_pt, en: tour.description_en, es: tour.description_es },
        card_teaser_pt: tour.card_teaser_pt || '', card_teaser_en: tour.card_teaser_en || '', card_teaser_es: tour.card_teaser_es || '',
        duration: tour.duration, departure: tour.departure,
        departure_date: tour.departure_date || '', arrival_date: tour.arrival_date || '',
        price: tour.base_price || tour.price, currency: tour.currency || 'USD',
        air_fare: tour.air_fare || 0, land_only_enabled: !!tour.land_only_enabled, is_past: !!tour.is_past,
        image: tour.image, image_card: tour.image_card, image_list_view: tour.image_list_view, featured: tour.featured, bestSeller: tour.bestSeller,
        status_tag: tour.status_tag || '', show_tag_homepage: tour.show_tag_homepage,
        visibility: tour.visibility || 'public',
        group_label_pt: tour.group_label_pt || '', group_label_en: tour.group_label_en || '', group_label_es: tour.group_label_es || '',
        guides: Array.isArray(tour.guides) ? tour.guides : [],
        highlights: tour.highlights_pt?.map((_, idx) => ({
          pt: tour.highlights_pt[idx], en: tour.highlights_en[idx], es: tour.highlights_es[idx],
        })) || [],
      })));
      setFaqData(faqsRes.data.map(faq => ({
        _id: faq._id,
        question: { pt: faq.question_pt, en: faq.question_en, es: faq.question_es },
        answer: { pt: faq.answer_pt, en: faq.answer_en, es: faq.answer_es },
        category: faq.category || 'before_booking',
      })));
      setTestimonials(Array.isArray(testimonialsRes) ? testimonialsRes : []);
      if (hpRes) setHpSettings(hpRes);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTours = useMemo(() => {
    let list = tourTab === 'all' ? tours : tours.filter(t => {
      const dep = Array.isArray(t.departure) ? t.departure : [t.departure];
      if (tourTab === 'brazil') return dep.some(d => d && (d.startsWith('br_') || d.startsWith('brazil')));
      if (tourTab === 'usa') return dep.some(d => d && d.startsWith('usa'));
      return dep.some(d => d && d.startsWith(tourTab));
    });
    const q = norm(tourQuery);
    if (q) {
      const tokens = q.split(' ').filter(Boolean);
      list = list.filter(tour => {
        const hay = buildTourHaystack(tour, language);
        return tokens.every(tok => hay.includes(tok));
      });
    }
    return list;
  }, [tours, tourTab, tourQuery, language]);

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: language === 'pt' ? 'Mensagem enviada!' : language === 'en' ? 'Message sent!' : '¡Mensaje enviado!',
      description: language === 'pt' ? 'Entraremos em contato em breve.' : language === 'en' ? 'We will contact you soon.' : 'Nos pondremos en contacto pronto.',
    });
    setFormData({ name: '', phone: '', email: '', message: '' });
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center py-32"><p className="text-xl" style={SERIF}>{language === 'pt' ? 'Preparando sua peregrinação…' : language === 'en' ? 'Preparing your pilgrimage…' : 'Preparando tu peregrinación…'}</p></div>;
  }

  const hp = (section, field) => {
    const s = hpSettings?.[section];
    return s?.[`${field}_${language}`] || t[section]?.[field] || '';
  };
  const hpReasons = hpSettings?.whyChoose?.reasons;
  const dynamicReasons = (hpReasons && hpReasons.length > 0)
    ? hpReasons.map(r => ({ title: r[`title_${language}`] || r.title_pt || '', description: r[`description_${language}`] || r.description_pt || '' }))
    : t.whyChoose.reasons;

  const TAB_LABELS = {
    all: { pt: 'Todas', en: 'All', es: 'Todas' },
    brazil: { pt: 'Brasil', en: 'Brazil', es: 'Brasil' },
    usa: { pt: 'EUA', en: 'USA', es: 'EE. UU.' },
  };

  const blocks = {
    spotlight: <HomeSpotlight />,
    hero: <Hero />,
    pilgrimages: (
      <section id="pilgrimages" className="py-28 lg:py-32 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10" data-section-head>
            <div className="text-[10px] tracking-[0.4em] uppercase mb-4" style={{ color: GOLD }}>
              {language === 'pt' ? '— Peregrinações 2026' : language === 'en' ? '— Pilgrimages 2026' : '— Peregrinaciones 2026'}
            </div>
            <h2 style={SERIF} className="text-5xl lg:text-7xl font-light leading-none" data-testid="home-tours-heading">
              {language === 'pt' ? 'Nossas Peregrinações' : language === 'en' ? 'Our Pilgrimages' : 'Nuestras Peregrinaciones'}<span style={{ color: GOLD }}>.</span>
            </h2>
          </div>

          {/* Filter bar — Iter 349c: same tidy layout as /pilgrimages.
              Mobile: search / chips+toggle / count+compare. Desktop: one row. */}
          <div className="mb-10 flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-4">
            <div className="relative lg:flex-1 lg:max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
              <input
                type="search"
                value={tourQuery}
                onChange={(e) => setTourQuery(e.target.value)}
                placeholder={language === 'pt' ? 'Buscar por destino, padre, data…' : language === 'en' ? 'Search by destination, priest, date…' : 'Buscar por destino, padre, fecha…'}
                aria-label={language === 'pt' ? 'Buscar peregrinações' : language === 'en' ? 'Search pilgrimages' : 'Buscar peregrinaciones'}
                className="w-full text-sm pl-9 pr-9 py-2.5 rounded-full border bg-white focus:outline-none focus:ring-2 transition"
                style={{ borderColor: `${alpha(GOLD, '55')}`, color: NAVY, '--tw-ring-color': `${alpha(GOLD, '66')}` }}
                data-testid="home-tours-search-input"
              />
              {tourQuery && (
                <button
                  type="button"
                  onClick={() => setTourQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-black/10 transition"
                  aria-label={language === 'pt' ? 'Limpar busca' : language === 'en' ? 'Clear search' : 'Limpiar búsqueda'}
                  data-testid="home-tours-search-clear"
                >
                  <X className="w-4 h-4 opacity-70" />
                </button>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 lg:justify-start lg:gap-3">
              <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none">
                {['all', 'brazil', 'usa'].map(k => (
                  <button
                    key={k}
                    onClick={() => setTourTab(k)}
                    className={`shrink-0 text-[10px] sm:text-[11px] tracking-[0.2em] uppercase px-3 sm:px-4 py-2 rounded-full border transition ${tourTab === k ? 'font-semibold' : 'hover:opacity-70'}`}
                    style={{
                      background: tourTab === k ? NAVY : 'transparent',
                      color: tourTab === k ? GOLD : NAVY,
                      borderColor: tourTab === k ? NAVY : `${alpha(GOLD, '55')}`,
                    }}
                    data-testid={`tab-${k}`}
                  >
                    {TAB_LABELS[k][language]}
                  </button>
                ))}
              </div>

              <div className="inline-flex items-center rounded-full border p-0.5 bg-white shrink-0" style={{ borderColor: `${alpha(GOLD, '55')}` }} data-testid="home-tours-view-toggle">
                <button
                  type="button"
                  onClick={() => chooseTourView('grid')}
                  className={`inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase rounded-full transition ${tourViewMode === 'grid' ? 'font-semibold' : 'hover:opacity-70'}`}
                  style={{ background: tourViewMode === 'grid' ? NAVY : 'transparent', color: tourViewMode === 'grid' ? GOLD : NAVY }}
                  aria-pressed={tourViewMode === 'grid'}
                  data-testid="home-tours-view-grid"
                  title={language === 'pt' ? 'Cartões' : language === 'en' ? 'Cards' : 'Tarjetas'}
                >
                  <LayoutGrid className="w-3.5 h-3.5" />
                  <span className="hidden lg:inline">{language === 'pt' ? 'Cartões' : language === 'en' ? 'Cards' : 'Tarjetas'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => chooseTourView('list')}
                  className={`inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase rounded-full transition ${tourViewMode === 'list' ? 'font-semibold' : 'hover:opacity-70'}`}
                  style={{ background: tourViewMode === 'list' ? NAVY : 'transparent', color: tourViewMode === 'list' ? GOLD : NAVY }}
                  aria-pressed={tourViewMode === 'list'}
                  data-testid="home-tours-view-list"
                  title={language === 'pt' ? 'Lista' : language === 'en' ? 'List' : 'Lista'}
                >
                  <List className="w-3.5 h-3.5" />
                  <span className="hidden lg:inline">{language === 'pt' ? 'Lista' : language === 'en' ? 'List' : 'Lista'}</span>
                </button>
              </div>
            </div>

            {/* Iter 356b — contador removido; só o link Comparar fica aqui */}
            <div className="flex items-center justify-end lg:ml-auto">
              <Link to="/comparar" className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] tracking-[0.2em] uppercase px-3 py-1.5 rounded-full border transition hover:bg-black/5" style={{ borderColor: `${alpha(GOLD, '55')}`, color: NAVY }} data-testid="home-compare-link">
                <ArrowLeftRight className="w-3 h-3" />
                {language === 'pt' ? 'Comparar' : language === 'en' ? 'Compare' : 'Comparar'}
              </Link>
            </div>
          </div>

          {tourViewMode === 'list' ? (
            <div className="flex flex-col gap-3" data-testid="home-tours-list">
              {filteredTours.map((tour) => <TourListRow key={tour.id} tour={tour} language={language} />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-8" data-testid="home-tours-grid">
              {filteredTours.map((tour) => <TourCard key={tour.id} tour={tour} />)}
            </div>
          )}

          {filteredTours.length === 0 && (
            <p className="text-center py-12 opacity-60">
              {tourQuery
                ? (language === 'pt' ? 'Nada encontrado para sua busca.' : language === 'en' ? 'Nothing matched your search.' : 'Nada coincide con tu búsqueda.')
                : (language === 'pt' ? 'Nenhuma peregrinação encontrada nesta categoria.' : language === 'en' ? 'No pilgrimages found in this category.' : 'No se encontraron peregrinaciones en esta categoría.')}
            </p>
          )}

          <div className="text-center mt-14">
            <Link to="/pilgrimages" className="inline-flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase font-medium px-9 py-5 border-2 transition hover:bg-[color:var(--navy)] hover:text-[color:var(--gold)]" style={{ borderColor: NAVY, color: NAVY, '--navy': NAVY, '--gold': GOLD }} data-testid="view-all-pilgrimages-btn">
              {language === 'pt' ? 'Ver Todas as Peregrinações' : language === 'en' ? 'View All Pilgrimages' : 'Ver Todas las Peregrinaciones'}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    ),
    gallery: <HomeGallery />,
    about: (
      <section id="about" className="py-32 px-6 lg:px-10" data-testid="about-section">
        <div className="max-w-7xl mx-auto grid grid-cols-12 gap-8 items-start">
          <div className="col-span-12 lg:col-span-5">
            <div className="relative" style={{ aspectRatio: '4/5' }}>
              <img src="/founders.jpg" alt="Marcos e Claudia Demarré" className="w-full h-full object-cover" data-testid="founders-photo" />
              <div className="absolute -bottom-6 -right-6 py-5 px-7 shadow-xl hidden md:block" style={{ background: NAVY, color: CREAM }}>
                <div className="text-5xl font-light leading-none" style={{ color: GOLD, ...SERIF }}>30+</div>
                <div className="text-[10px] tracking-[0.3em] uppercase mt-2 opacity-80">{language === 'pt' ? 'anos de peregrinação' : language === 'en' ? 'years of pilgrimage' : 'años de peregrinación'}</div>
              </div>
            </div>
            <p className="text-center text-sm opacity-60 mt-6 italic" style={SERIF}>Marcos e Claudia Demarré · Fundadores</p>
          </div>
          <div className="col-span-12 lg:col-span-7 lg:pl-14" data-section-head>
            <div className="text-[10px] tracking-[0.4em] uppercase mb-5" style={{ color: GOLD }}>— {language === 'pt' ? 'Nossa História · desde 1996' : language === 'en' ? 'Our History · since 1996' : 'Nuestra Historia · desde 1996'}</div>
            <h2 style={SERIF} className="text-4xl lg:text-6xl font-light leading-[1.05] mb-7" data-testid="about-title">{hp('about', 'title') || t.about.title}</h2>
            <h3 className="text-sm tracking-[0.3em] uppercase mb-5 inline-flex items-center gap-2" style={{ color: GOLD }}>
              <Cross className="w-4 h-4" /> {hp('about', 'history_title') || t.about.history_title}
            </h3>
            <p className="text-base text-black/70 leading-relaxed mb-4">{hp('about', 'history1') || t.about.history1}</p>
            <p className="text-base text-black/70 leading-relaxed mb-4">{hp('about', 'history2') || t.about.history2}</p>
            <p className="text-base text-black/70 leading-relaxed mb-10">{hp('about', 'history3') || t.about.history3}</p>
            <div className="p-10 relative" style={{ background: NAVY, color: CREAM }}>
              <Quote className="w-8 h-8 absolute top-6 right-6 opacity-30" style={{ color: GOLD }} />
              <h3 style={SERIF} className="text-3xl lg:text-4xl font-light mb-5">{hp('about', 'pilgrimage_title') || t.about.pilgrimage_title}</h3>
              <p className="text-sm text-white/80 leading-relaxed mb-3">{hp('about', 'description1') || t.about.description1}</p>
              <p className="text-sm text-white/80 leading-relaxed mb-3">{hp('about', 'description2') || t.about.description2}</p>
              <p className="text-sm text-white/80 leading-relaxed mb-7 italic" style={{ color: GOLD }}>{hp('about', 'description3') || t.about.description3}</p>
              <a href={`https://api.whatsapp.com/send?phone=${whatsappNum.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase font-semibold px-7 py-4 transition hover:opacity-90" style={{ background: GOLD, color: NAVY }}>
                {hp('about', 'cta') || t.about.cta} <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>
    ),
    testimonials: testimonials.length > 0 && (
        <section id="testimonials" className="py-32 px-6 lg:px-10" style={{ background: NAVY, color: CREAM }} data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16" data-section-head>
              <div className="text-[10px] tracking-[0.4em] uppercase mb-4" style={{ color: GOLD }}>— Palavras dos nossos peregrinos</div>
              <h2 style={SERIF} className="text-5xl lg:text-6xl font-light leading-tight" data-testid="testimonials-title">
                {language === 'pt' ? 'Depoimentos' : language === 'en' ? 'Testimonials' : 'Testimonios'}<span style={{ color: GOLD }}>.</span>
              </h2>
              {/* Iter 359 — Trust-building subtitle before the testimonials grid. */}
              <p className="mt-6 max-w-2xl mx-auto text-base lg:text-lg opacity-80 italic leading-relaxed" style={{ fontFamily: '"Inter", sans-serif' }} data-testid="testimonials-subtitle">
                {language === 'pt'
                  ? 'Milhares de peregrinos viajaram com a Demarré desde 1996. Veja o que eles dizem!'
                  : language === 'en'
                    ? 'Thousands of pilgrims have travelled with Demarré since 1996. See what they say!'
                    : '¡Miles de peregrinos han viajado con Demarré desde 1996. Vea lo que dicen!'}
              </p>
            </div>
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
              {testimonials.slice(0, 6).map((tst) => {
                const text = tst[`text_${language}`] || tst.text_pt || '';
                return (
                  <article key={tst._id} className="group p-7 flex flex-col" style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${alpha(GOLD, '22')}` }} data-testid={`testimonial-public-${tst._id}`}>
                    {tst.media_type === 'image' && tst.media_url && (
                      <div className="h-48 overflow-hidden mb-5 -mx-7 -mt-7"><img src={`${process.env.REACT_APP_BACKEND_URL}${tst.media_url}`} alt={tst.author_name} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" /></div>
                    )}
                    {tst.media_type === 'video' && tst.media_url && (
                      <div className="h-48 relative mb-5 -mx-7 -mt-7 cursor-pointer" onClick={() => setVideoPopup(`${process.env.REACT_APP_BACKEND_URL}${tst.media_url}`)}>
                        <video src={`${process.env.REACT_APP_BACKEND_URL}${tst.media_url}`} className="w-full h-full object-cover opacity-60" muted playsInline />
                        <div className="absolute inset-0 flex items-center justify-center"><div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: GOLD }}><Play className="h-6 w-6 ml-0.5" style={{ color: NAVY }} fill="currentColor" /></div></div>
                      </div>
                    )}
                    <Quote className="h-6 w-6 mb-4" style={{ color: GOLD }} />
                    <p style={SERIF} className="text-xl font-light italic leading-snug mb-5 flex-1">"{text}"</p>
                    <div className="pt-5 border-t" style={{ borderColor: `${alpha(GOLD, '33')}` }}>
                      <div className="text-sm font-semibold">{tst.author_name}</div>
                      <div className="text-[10px] tracking-[0.3em] uppercase mt-1 opacity-60">
                        {tst.author_location && <span>{tst.author_location}</span>}
                        {tst.author_location && tst.tour_name && <span> · </span>}
                        {tst.tour_name && <span>{tst.tour_name}</span>}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
    ),
    faq: (
      <FAQSection
        faqData={faqData}
        language={language}
        SERIF={SERIF}
        GOLD={GOLD}
        NAVY={NAVY}
        onFaqOpen={(id) => {
          // Session-dedupe analytics ping so one pilgrim refreshing the home
          // doesn't inflate the open-count.
          try {
            const key = `demarre-faq-opened-${id}`;
            if (sessionStorage.getItem(key)) return;
            sessionStorage.setItem(key, '1');
            api.trackFAQView(id);
          } catch { /* ignore */ }
        }}
      />
    ),
    contact: (
      <section id="contact" className="py-32 px-6 lg:px-10" style={{ background: `linear-gradient(to bottom, ${CREAM}, ${ALT_BG})` }} data-testid="contact-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14" data-section-head>
            <div className="text-[10px] tracking-[0.4em] uppercase mb-4" style={{ color: GOLD }}>— {t.contact.subtitle}</div>
            <h2 style={SERIF} className="text-5xl lg:text-6xl font-light leading-tight mb-5">{t.contact.title}</h2>
            <p className="text-base opacity-60 max-w-xl mx-auto">{t.contact.description}</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="p-10 space-y-7" style={{ background: '#fff', border: `1px solid ${alpha(GOLD, '44')}` }}>
              <div>
                <div className="text-[10px] tracking-[0.4em] uppercase mb-2 flex items-center gap-2" style={{ color: GOLD }}><MapPin className="w-3 h-3" /> {brOffice.name[language]}</div>
                <p className="text-sm opacity-70 leading-relaxed">{brOffice.address}<br/>{brOffice.city}</p>
                <p className="text-sm font-medium mt-1" style={{ color: NAVY }}>{brOffice.phone}</p>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.4em] uppercase mb-2 flex items-center gap-2" style={{ color: GOLD }}><MapPin className="w-3 h-3" /> {usOffice.name[language]}</div>
                <p className="text-sm opacity-70 leading-relaxed">{usOffice.address}<br/>{usOffice.city}</p>
                <p className="text-sm font-medium mt-1" style={{ color: NAVY }}>{usOffice.phone}</p>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.4em] uppercase mb-2 flex items-center gap-2" style={{ color: GOLD }}><Mail className="w-3 h-3" /> Email</div>
                <p className="text-sm font-medium">{companyInfo.email}</p>
              </div>
              <a href={`https://api.whatsapp.com/send?phone=${whatsappNum.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 w-full justify-center text-[11px] tracking-[0.3em] uppercase font-semibold px-6 py-4 transition hover:opacity-90" style={{ background: NAVY, color: GOLD }}>
                <MessageCircle className="w-4 h-4" />
                {language === 'pt' ? 'Falar no WhatsApp' : language === 'en' ? 'Chat on WhatsApp' : 'Hablar por WhatsApp'}
              </a>
            </div>
            <form onSubmit={handleSubmit} className="p-10 space-y-5" style={{ background: '#fff', border: `1px solid ${alpha(GOLD, '44')}` }} data-testid="contact-form">
              <div>
                <label className="text-[10px] tracking-[0.3em] uppercase mb-2 block opacity-70">{t.contact.name}</label>
                <input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="w-full bg-transparent border-b py-3 focus:outline-none focus:border-[color:var(--gold)]" style={{ borderColor: `${alpha(NAVY, '33')}`, '--gold': GOLD }} />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.3em] uppercase mb-2 block opacity-70">{t.contact.phone}</label>
                <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required className="w-full bg-transparent border-b py-3 focus:outline-none focus:border-[color:var(--gold)]" style={{ borderColor: `${alpha(NAVY, '33')}`, '--gold': GOLD }} />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.3em] uppercase mb-2 block opacity-70">{t.contact.email}</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="w-full bg-transparent border-b py-3 focus:outline-none focus:border-[color:var(--gold)]" style={{ borderColor: `${alpha(NAVY, '33')}`, '--gold': GOLD }} />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.3em] uppercase mb-2 block opacity-70">{t.contact.message}</label>
                <textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} required rows={5} className="w-full bg-transparent border-b py-3 focus:outline-none focus:border-[color:var(--gold)] resize-none" style={{ borderColor: `${alpha(NAVY, '33')}`, '--gold': GOLD }} />
              </div>
              <button type="submit" className="w-full inline-flex items-center justify-center gap-3 text-[11px] tracking-[0.35em] uppercase font-semibold px-6 py-5 transition hover:opacity-90" style={{ background: GOLD, color: NAVY }} data-testid="contact-submit-btn">
                {t.contact.send} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </section>
    ),
  };

  return (
    <div className="min-h-screen" style={{ background: CREAM, color: INK }}>
      {homeSections.filter(s => s.visible && blocks[s.key]).map(s => <div key={s.key} data-home-section={s.key}>{blocks[s.key]}</div>)}

      {videoPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80" onClick={() => setVideoPopup(null)}>
          <div className="relative max-w-3xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setVideoPopup(null)} className="absolute -top-10 right-0 text-white hover:text-gray-300" data-testid="close-video-popup"><X className="h-6 w-6" /></button>
            <video src={videoPopup} controls autoPlay className="w-full shadow-2xl" style={{ maxHeight: '80vh' }} />
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Iter 371c — Home FAQ preview (first 5 collapsed + CTA to /faqs) ─────
const FAQSection = ({ faqData, language, SERIF, GOLD, NAVY, onFaqOpen }) => {
  // Iter 371c — Filter out FAQs with empty question for the current language
  // (some topics only exist in PT, EN or ES — see seed_faqs_iter371c.py).
  const visible = faqData.filter(f => (f.question[language] || '').trim());
  // Home shows only the first 5 collapsed — users click "Ver todas" to open
  // the full /faqs page where every answer is already expanded.
  const PREVIEW_LIMIT = 5;
  const preview = visible.slice(0, PREVIEW_LIMIT);
  if (preview.length === 0) return null;
  const hasMore = visible.length > PREVIEW_LIMIT;
  return (
    <section id="faq" className="py-32 px-6 lg:px-10" data-testid="faq-section">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10" data-section-head>
          <div className="text-[10px] tracking-[0.4em] uppercase mb-4" style={{ color: GOLD }}>— {language === 'pt' ? 'Perguntas Frequentes' : language === 'en' ? 'Frequently Asked Questions' : 'Preguntas Frecuentes'}</div>
          <h2 style={SERIF} className="text-5xl lg:text-6xl font-light leading-tight">
            {language === 'pt' ? 'Esclarecemos tudo antes' : language === 'en' ? 'We clarify everything first' : 'Aclaramos todo antes'}<span style={{ color: GOLD }}>.</span>
          </h2>
        </div>
        <Accordion
          type="single"
          collapsible
          className="divide-y"
          style={{ borderColor: `${alpha(NAVY, '22')}` }}
          data-testid="faq-home-accordion"
          onValueChange={(val) => {
            // Iter 371d — Analytics: fire open-count ping when a FAQ accordion
            // panel opens. Session-dedupe (one ping per FAQ per page load) via
            // the `trackedFaqs` state in Home so admins see meaningful numbers.
            if (!val) return;
            try {
              const idx = parseInt(String(val).replace('item-', ''), 10);
              const faq = preview[idx];
              if (faq?._id) onFaqOpen?.(faq._id);
            } catch { /* ignore */ }
          }}
        >
          {preview.map((faq, i) => (
            <AccordionItem key={`home-faq-${i}`} value={`item-${i}`} className="border-t" style={{ borderColor: `${alpha(NAVY, '22')}`, borderRadius: 0 }}>
              <AccordionTrigger className="text-left hover:no-underline py-6 group">
                <span style={SERIF} className="text-xl lg:text-2xl font-normal leading-tight pr-6">{faq.question[language]}</span>
              </AccordionTrigger>
              <AccordionContent className="pb-6 text-base leading-relaxed whitespace-pre-line" style={{ color: 'color-mix(in srgb, var(--site-navy) 70%, transparent)' }}>{faq.answer[language]}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        {hasMore && (
          <div className="mt-10 text-center">
            <Link
              to="/faqs"
              className="inline-flex items-center justify-center gap-3 text-[11px] tracking-[0.35em] uppercase font-semibold px-8 py-4 transition hover:opacity-90"
              style={{ background: NAVY, color: GOLD }}
              data-testid="faq-see-all-btn"
            >
              {language === 'pt' ? `Ver todas as ${visible.length} perguntas` : language === 'en' ? `View all ${visible.length} FAQs` : `Ver las ${visible.length} preguntas`}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default Home;
